import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  FaChevronDown, FaChevronUp,
} from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import _, { isEqual, isEqualWith } from 'lodash';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import FlightDetails from '../FlightDetails';
import {
  addToTrip, removeFromTrip, resetTrips, saveTripRequest,
  setSelfBookingAllowed,
} from '../../../../store/slices/trip';
import FlightFareClasses from './FlightFareClasses';
import { flightSearch, updateFlightSearchObj, initDirectSellFlightSearchRequest } from '../../../../store/slices/flight';
import FlightAddUpdateToBrochure from '../../AddUpdateBrochure/FlightAddUpdateToBrochure';
import FlightsSeatMap from './FlightsSeatMap';
import {
  FlightSearchTypes,
  PageLinks, SearchModes, SearchStatus, TravelTypes,
} from '../../../../constants';
import FlightInfo from './FlightInfo';
import useScrollToElementById from '../../../../hooks/useScrollToElementById';
import getElementId from '../../../../helpers/getElementId';
import { setFlightTripSearchParam } from '../../../../store/slices/tripSearch';
import checkDoEnableTripConfigDecoupling from '../../../../helpers/selectors/checkDoEnableTripConfigDecoupling';
import flightSearchInputSelector from '../../../../helpers/selectors/flightSearchInput';
import { checkDoEnableSelectFlightsBySegment } from '../../../../store/slices/user';
import formatTripIdForBrochure from '../../../../helpers/formatters/formatTripIdForBrochure';
import checkIsNdcFare from '../../../../helpers/checkNdcFare';
import checkIsFlightTripSearch from '../../../../helpers/selectors/checkIsFlightTripSearch';

const FlightItinerary = ({
  itinerary, tripId, onModalClose, doShowPrice, isModalOpen, doCheckoutOnSelect,
  doHaveBorderAround, itineraryIndex, tripRoomSearchInput,
}) => {
  const router = useRouter();
  const { pageName } = router.query;
  const { tripConfirmation, selectedTrips } = useSelector((state) => state.trip);
  const { isLoggedIn } = useSelector((state) => state.user);
  const { isViewingBrochure, updateBrochurePriceStatus } = useSelector((state) => state.brochure);
  const { selectedSearchMode } = useSelector((state) => state.tripSearch);
  const isTripSearch = selectedSearchMode === SearchModes.FLIGHT;
  const trip = selectedTrips && tripId && selectedTrips[tripId];
  const saveTripStatus = trip && trip.saveTripStatus;
  const isConfirmation = !doCheckoutOnSelect && tripConfirmation != null;
  const isConfirmationPage = router.pathname.indexOf('confirmation') > -1;
  const { staterooms } = useSelector((store) => store.tripSearch.searchObj);
  const dispatch = useDispatch();
  const selected = useMemo(() => (trip
    && trip.flightItinerary
    && trip.flightItinerary.flightKey === itinerary.flightKey) === true,
  [trip, itinerary]);

  const scrollToElement = useScrollToElementById();

  const doEnableTripConfigDecoupling = useSelector(checkDoEnableTripConfigDecoupling);
  const isFlightTripSearch = useSelector(checkIsFlightTripSearch(isTripSearch));
  const {
    selectedFlightSegments, breadCrumbsIndex, searchObj,
  } = useSelector(flightSearchInputSelector(tripId, isFlightTripSearch));
  const isBrochurePage = router.pathname.indexOf('brochure') > -1;
  const { travelAgency = {} } = useSelector((state) => state.user);
  const brochureSettings = travelAgency?.settings?.brochureSettings || {};
  const doExpandItineraryDetailsByDefault = (!!brochureSettings?.doExpandItineraryDetailsByDefault
  && isBrochurePage) || (searchObj?.flightSearchType === FlightSearchTypes.DIRECT_SELL);
  const [isDetailsOpen, setIsDetailsOpen] = useState(doExpandItineraryDetailsByDefault);

  const { flightSegments, flightSearchType } = searchObj || {};
  let doEnableSelectFlightsBySegment = useSelector(checkDoEnableSelectFlightsBySegment);
  if (flightSearchType === FlightSearchTypes.DIRECT_SELL
    || flightSearchType === FlightSearchTypes.ONE_WAY) {
    doEnableSelectFlightsBySegment = false;
  }

  const handleRemoveFromTrip = useCallback(() => {
    dispatch(removeFromTrip({ tripId, key: 'flightItinerary' }));
    if (doEnableSelectFlightsBySegment) {
      if (selectedFlightSegments[breadCrumbsIndex]) {
        const selectedFlightSegments2 = { ...selectedFlightSegments }; // shallow clone
        delete selectedFlightSegments2[breadCrumbsIndex];
        dispatch(setFlightTripSearchParam({
          selectedFlightSegments: selectedFlightSegments2,
        }));
      }
    }
  }, [breadCrumbsIndex, dispatch, doEnableSelectFlightsBySegment, selectedFlightSegments, tripId]);

  const handleDetailsClick = () => {
    setIsDetailsOpen((v) => !v);
  };

  const handleScroll = useCallback((event) => {
    if (!event) return;
    if (doEnableTripConfigDecoupling) {
      const containerWrapper = document.getElementById('main-section-wrapper');
      containerWrapper.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    const elementId = getElementId(event, '.parentDiv');
    if (elementId) {
      scrollToElement(elementId, 'banner');
    } else {
      scrollToElement(tripId, 'banner');
    }
  }, [doEnableTripConfigDecoupling, scrollToElement, tripId]);

  const findNextAvailableSegmentIndex = () => {
    // Forward search
    for (let i = breadCrumbsIndex + 1; i < flightSegments.length; i += 1) {
      if (!selectedFlightSegments[i]) return i;
    }

    // Backward search only if no match found
    for (let i = breadCrumbsIndex - 1; i >= 0; i -= 1) {
      if (!selectedFlightSegments[i]) return i;
    }

    return null;
  };
  const customizer = (objValue, otherValue, key) => {
    if (key === 'offerItemIds') {
      return true; // skip comparing this field
    }
    return undefined;
  };

  const handleAddToTripClick = (event, fareIndex) => {
    if (tripId) {
      const flightItinerary = JSON.parse(JSON.stringify(itinerary)); // Clone
      const isNdcFare = checkIsNdcFare(flightItinerary);
      const isSelectedFareChanged = fareIndex > -1
        && (flightItinerary.flightSegments[0].flights[0].allFares[fareIndex].price
          !== flightItinerary.flightSegments[0].flights[0].selectedFare.price
          || flightItinerary.flightSegments[0].flights[0].allFares[fareIndex].fareBasisCode
          !== flightItinerary.flightSegments[0].flights[0].selectedFare.fareBasisCode
          || flightItinerary.flightSegments[0].flights[0].allFares[fareIndex].pricingSubsource
          !== flightItinerary.flightSegments[0].flights[0].selectedFare.pricingSubsource
          || flightItinerary.flightSegments[0].flights[0].allFares[fareIndex].fareBrandId
          !== flightItinerary.flightSegments[0].flights[0].selectedFare.fareBrandId
          || flightItinerary.flightSegments[0].flights[0].allFares[fareIndex].isRefundable
          !== flightItinerary.flightSegments[0].flights[0].selectedFare.isRefundable);

      if (doEnableSelectFlightsBySegment) {
        const nextIndex = findNextAvailableSegmentIndex();
        const flightSegmentIndex = flightItinerary.flightSegments.length === 1
          ? 0 : breadCrumbsIndex;

        if (selectedFlightSegments[breadCrumbsIndex]
          && isEqualWith(itinerary.flightSegments[flightSegmentIndex],
            selectedFlightSegments[breadCrumbsIndex], customizer)) {
          const selectedFlightSegments2 = { ...selectedFlightSegments }; // shallow clone
          delete selectedFlightSegments2[breadCrumbsIndex];
          dispatch(setFlightTripSearchParam({
            selectedFlightSegments: selectedFlightSegments2,
          }));
          return;
        }
        const updatedSelectedFlightSegments = {
          ...selectedFlightSegments,
        };
        updatedSelectedFlightSegments[breadCrumbsIndex] = {
          ...flightItinerary.flightSegments[flightSegmentIndex],
          selectedFareIndex: fareIndex > -1 ? fareIndex : undefined,
        };
        if (isTripSearch) {
          dispatch(setFlightTripSearchParam({
            selectedFlightSegments: updatedSelectedFlightSegments,
          }));
        } else {
          dispatch(updateFlightSearchObj({
            tripId,
            searchObj: {
              selectedFlightSegments: updatedSelectedFlightSegments,
            },
          }));
        }

        if (breadCrumbsIndex === flightSegments.length - 1) {
          // When last segment is selected then add flight itinerary to trip
          if (isSelectedFareChanged) {
            // NOTE: Commenting out following code to call dispatch(flightSearch) to set selected
            // fare with flight itinerary and update results page with selected fare

            // // Call /validate endpoint to set selected fare with flight itinerary
            // dispatch(validateFlightSegmentsRequest({
            //   flightItinerary,
            //   selectedFareIndex: fareIndex,
            //   tripId,
            //   flightKey: flightItinerary.flightKey,
            // }));
            // return;
          }
        } else {
          // Reset pagination if current page is not 1
          const searchObjUpdate = { breadCrumbsIndex: nextIndex };
          if (searchObj && searchObj.page && searchObj.page !== 1) {
            searchObjUpdate.page = 1;
          }
          if (isTripSearch) dispatch(resetTrips());
          dispatch(flightSearch({
            tripId,
            isTripSearch: isFlightTripSearch,
            searchObj: searchObjUpdate,
          }));
          return;
        }
      }

      // Call /flightSearch endpoint to set selected fare with flight itinerary
      // and update results page with selected fare
      if (isSelectedFareChanged || isNdcFare) {
        flightItinerary.cost = null; // Removing cost, to show spinner in trip configuration
        const flight = flightItinerary.flightSegments[0]?.flights[0];
        const selectedFare = flight?.selectedFare;
        dispatch(addToTrip({
          tripId,
          trip: {
            flightItinerary,
          },
        }));

        const flightSearchRequest = {
          tripId,
          doAddSelectedFlightToTrip: true,
          flightItinerary: itinerary,
          isTripSearch: selectedSearchMode === SearchModes.FLIGHT,
          searchObj: {
            selectedFlightKey: flightItinerary.flightKey,
            selectedFlightSelectedFareIndex: isNdcFare && _.isNil(fareIndex)
              ? flight.allFares.findIndex((fare) => isEqual(fare, selectedFare)) : fareIndex,
          },
        };

        if (flightSearchType === FlightSearchTypes.DIRECT_SELL) {
          dispatch(initDirectSellFlightSearchRequest(flightSearchRequest));
        } else {
          dispatch(flightSearch(flightSearchRequest));
        }
      } else {
        dispatch(addToTrip({
          tripId,
          trip: {
            flightItinerary,
          },
        }));
      }
      if (onModalClose) onModalClose(event);
      handleScroll(event);
    }
  };

  const handleCheckout = (event, fareIndex) => {
    if (isBrochurePage) {
      // DISABLED CHECKOUT FOR NDC FARES FROM BROCHURE
      if (checkIsNdcFare(itinerary, fareIndex)) {
        return;
      }
      dispatch(setSelfBookingAllowed());
    }
    if (saveTripStatus === SearchStatus.SEARCHING) return;
    const flightTripSeg = tripConfirmation && tripConfirmation.trip
      && _.find(tripConfirmation.trip.tripSegments, (ts) => ts.mode === TravelTypes.flight);
    const request = {
      tripId,
      ignoreUpdateTripOnSuccess: true,
      trip: {
        tripId,
        flightItinerary: {
          ...itinerary,
          tripSegmentId: (flightTripSeg || {}).id,
        },
        selectedFlightFareIndex: fareIndex >= 0 ? fareIndex : null,
        doChangeItineraryInBookedTrip: pageName === PageLinks.ChangeFlight.pageName,
        doAddItineraryInBookedTrip: pageName === PageLinks.AddFlight.pageName,
      },
      tripRoomSearchInput,
    };
    dispatch(saveTripRequest(request));
    if (onModalClose) onModalClose();
  };

  const showHideFlightDetailElement = (
    <button onClick={handleDetailsClick} type="button" className="text-theme">
      {!isDetailsOpen && (
        <span>
          View Details
          <FaChevronDown className="inline-block ml-1" />
        </span>
      )}
      {isDetailsOpen && (
        <span>
          Hide Details
          <FaChevronUp className="inline-block ml-1" />
        </span>
      )}
    </button>
  );

  const handleSetInteracted = () => {
    const payload = { hasUserInteractedWithFlightList: true, tripId };
    if (isTripSearch) dispatch(setFlightTripSearchParam(payload));
    else dispatch(updateFlightSearchObj(payload));
  };

  const handleMouseEnter = () => {
    if (itineraryIndex >= 4) {
      handleSetInteracted();
    }
  };

  const brochure = useSelector((state) => state.brochure.brochures[tripId]);
  const doAllowBook = brochure && brochure.doAllowBook;

  const getHandleAddToTripClick = () => {
    if (doCheckoutOnSelect) return handleCheckout;
    if (isBrochurePage) {
      if (doAllowBook || isLoggedIn) return handleCheckout;
      return undefined;
    }
    return handleAddToTripClick;
  };

  return (
    <div onMouseEnter={handleMouseEnter} onClick={handleSetInteracted} tabIndex={0} onKeyDown={() => { }} role="button" className={clsx('my-3 rounded-lg sm:p-3 p-2 cursor-default', { 'border-2': doHaveBorderAround, 'border-theme border-opacity-50 bg-theme bg-opacity-10': doHaveBorderAround && selected })}>
      <FlightInfo
        itinerary={itinerary}
        tripId={tripId}
        doShowPrice={doShowPrice}
        doCheckoutOnSelect={doCheckoutOnSelect}
        onCheckout={handleCheckout}
        selected={selected}
        onAddToTripClick={handleAddToTripClick}
        onRemoveFromTrip={handleRemoveFromTrip}
        isConfirmation={isConfirmation}
        showHideFlightDetailElement={showHideFlightDetailElement}
        isViewingBrochure={isViewingBrochure}
        updateBrochurePriceStatus={updateBrochurePriceStatus}
      />
      {!isConfirmationPage && tripId && (
        <div className={clsx('flex justify-end flex-wrap text-theme', { 'xl:pr-8': isModalOpen || !(trip || {}).flightItinerary })}>
          <FlightAddUpdateToBrochure
            className=""
            tripId={formatTripIdForBrochure(tripId,
              itinerary.startDateTime, itinerary.endDateTime, staterooms)}
            flightItinerary={itinerary}
            tripRoomSearchInput={tripRoomSearchInput}
          />
          <div className="inline-block text-center text-right ml-5">
            {showHideFlightDetailElement}
          </div>
          <FlightFareClasses
            flightItinerary={itinerary}
            handleAddToTripClick={getHandleAddToTripClick()}
          />
          <FlightsSeatMap flights={_.flatMap(itinerary.flightSegments || [],
            (seg) => seg.flights)}
          />
        </div>
      )}

      <div>
        {isDetailsOpen && (
          <FlightDetails
            tripId={tripId}
            itinerary={itinerary}
            doShowPrice={doShowPrice}
            selected={selected}
            isConfirmation={isConfirmation}
          />
        )}
      </div>
    </div>
  );
};

FlightItinerary.propTypes = {
  itinerary: PropTypes.objectOf(PropTypes.objectOf),
  tripId: PropTypes.string,
  onModalClose: PropTypes.func,
  doShowPrice: PropTypes.bool,
  isModalOpen: PropTypes.bool,
  doCheckoutOnSelect: PropTypes.bool,
  doHaveBorderAround: PropTypes.bool,
  itineraryIndex: PropTypes.number,
  tripRoomSearchInput: PropTypes.arrayOf(PropTypes.shape()),
};

FlightItinerary.defaultProps = {
  itinerary: {},
  tripId: '',
  onModalClose: undefined,
  doShowPrice: true,
  isModalOpen: false,
  doCheckoutOnSelect: false,
  doHaveBorderAround: true,
  itineraryIndex: undefined,
  tripRoomSearchInput: undefined,
};

export default React.memo(FlightItinerary);
